import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Download, MapPin } from 'lucide-react';
import { api, API } from '../lib/api';
import { useLang } from '../i18n';
import Seo from '../components/Seo';
import { MonogramPattern } from '../components/Brush';
import { ScorePanel, Thumb, TableComposer } from '../components/TableComposer';
import { ShareBar } from '../components/ShareBar';

export const TableBuilder = () => {
  const { t } = useLang();

  return (
    <>
      <Seo title={t('build.kicker')} description={t('build.lead')} path="/componi" />

      <header className="py-14 md:py-24">
        <div className="wrap">
          <h1 className="font-display text-[clamp(2.5rem,5.5vw,5rem)] leading-[1] tracking-[-0.02em]">{t('build.title')}</h1>
          <p className="prosa mt-8 border-t-2 border-inchiostro pt-6 text-lg">{t('build.lead')}</p>
        </div>
      </header>

      <TableComposer />
    </>
  );
};

export const TableShared = () => {
  const { code } = useParams();
  const { t, lang } = useLang();
  const [data, setData] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    api
      .get(`/tables/${code}?lang=${lang}`)
      .then((r) => setData(r.data))
      .catch(() => setMissing(true));
  }, [code, lang]);

  if (missing) {
    return (
      <div className="wrap py-32">
        <h1 className="display-xl max-w-[16ch]">{t('build.notFound')}</h1>
        <Link to="/componi" className="btn btn-solid mt-9" data-testid="build-yours-btn">
          {t('build.buildYours')}
        </Link>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="wrap py-32">
        <p className="font-display text-3xl text-inchiostro/40">{t('common.loading')}</p>
      </div>
    );
  }

  const imageUrl = `${API}/tables/${code}/image.png?lang=${lang}`;

  return (
    <>
      <Seo
        title={t('build.sharedTitle')(data.shade.name)}
        description={data.verdict}
        image={imageUrl}
        path={`/tavola/${code}`}
      />

      <header
        data-testid="table-shared"
        className="relative overflow-hidden text-white"
        style={{ backgroundColor: data.shade.hex }}
      >
        <MonogramPattern className="absolute inset-0" opacity={0.06} />
        <div className="wrap relative py-14 md:py-24">
          <p className="text-[14px] font-bold text-white/80">
            {t('build.kicker')} · {code}
          </p>
          <h1 className="display-xl mt-6 !text-white">{t('build.sharedTitle')(data.shade.name)}</h1>
          <p className="mt-6 max-w-[46ch] text-base leading-relaxed text-white/85">
            {t('build.sharedLead')}
          </p>
        </div>
      </header>

      <section className="py-12 md:py-20">
        <div className="wrap">
          <div className="grid grid-cols-2 gap-x-4 gap-y-9 md:grid-cols-3 lg:grid-cols-6">
            {data.items.map((p) => (
              <Link
                key={p.sku}
                to={`/prodotti/${p.slug}`}
                data-testid={`shared-item-${p.sku}`}
                className="group"
              >
                <div className="aspect-square overflow-hidden bg-carta-2">
                  <Thumb product={p} />
                </div>
                <p className="mt-3 font-display text-base leading-snug group-hover:text-marsala">
                  {p.title.replace(/,\s*[^,]+$/, '')}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 md:py-20">
        <div className="wrap grid gap-12 lg:grid-cols-[1fr_1fr] lg:gap-16">
          <div>
            <p className="accento mb-7 text-2xl text-inchiostro/85">{t('build.rulesTitle')}</p>
            <ScorePanel score={data} hex={data.shade.hex} testid="shared-score" />
          </div>
          <div>
            <div className="aspect-[4/3] overflow-hidden bg-carta-2">
              <img src={imageUrl} alt={data.shade.name} data-testid="shared-image" className="w-full" />
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href={`${imageUrl}&download=1`} data-testid="shared-download" className="btn btn-line">
                <Download size={13} strokeWidth={2} />
                {t('build.download')}
              </a>
              <Link to="/componi" data-testid="build-yours-btn" className="btn btn-solid">
                {t('build.buildYours')}
              </Link>
            </div>
            <ShareBar
              url={window.location.href}
              title={t('build.sharedTitle')(data.shade.name)}
              image={new URL(imageUrl, window.location.origin).href}
              testid="shared-share"
              className="mt-8"
            />
            <Link to="/dove-comprare" data-testid="shared-cta-where" className="btn btn-line mt-10">
              <MapPin size={13} strokeWidth={2.2} />
              {t('common.findRetailer')}
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};
